import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PackageCard } from '../components/PackageCard';
import { MapPin, Package, ArrowLeft, ChevronRight, Plane, Calendar, Euro, Star } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DestinationPage() {
  const { destinationSlug } = useParams();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set document title based on destination
  useEffect(() => {
    if (destination) {
      document.title = destination.name + ' - Pachete Turistice 2026 | VaAi Travel';
    } else if (error) {
      document.title = 'Destinație negăsită | VaAi Travel';
    }
  }, [destination, error]);

  useEffect(() => {
    const fetchDestination = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL + '/api/seo/destination/' + destinationSlug);
        
        if (!response.ok) {
          throw new Error('Destinația nu a fost găsită');
        }
        
        const data = await response.json();
        setDestination(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (destinationSlug) {
      fetchDestination();
    }
  }, [destinationSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center pt-20">
        <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !destination) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center pt-20">
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Destinația nu a fost găsită</h1>
          <Link to="/" className="text-gold hover:underline">Înapoi la pagina principală</Link>
        </div>
      </div>
    );
  }

  // Calculate stats
  const priceMin = destination.packages?.length > 0 
    ? Math.min(...destination.packages.map(p => parseFloat(p.price?.replace(/[^\d]/g, '') || 999999)).filter(p => p < 999999))
    : null;
  
  const transportTypes = [...new Set(destination.packages?.map(p => p.transport_type).filter(Boolean) || [])];

  // SEO data
  const metaDescription = 'Descoperă ' + destination.count + ' pachete turistice în ' + destination.name + '. Circuite, sejururi și city break-uri la cele mai bune prețuri. Rezervă online!';
  const metaKeywords = 'vacanta ' + destination.name + ', circuit ' + destination.name + ', sejur ' + destination.name;
  const canonicalUrl = 'https://vaai.ro/destinatii/' + destinationSlug;
  const ogTitle = destination.name + ' - Pachete Turistice | VaAi Travel';
  const ogDescription = destination.count + ' pachete de vacanță în ' + destination.name;

  return (
    <div className="min-h-screen bg-navy pt-20">
      <Helmet>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <ol className="flex items-center space-x-2 text-sm">
          <li>
            <Link to="/" className="text-white/60 hover:text-gold transition-colors">Acasă</Link>
          </li>
          <ChevronRight className="w-4 h-4 text-white/40" />
          <li>
            <Link to="/destinatii" className="text-white/60 hover:text-gold transition-colors">Destinații</Link>
          </li>
          <ChevronRight className="w-4 h-4 text-white/40" />
          <li>
            <span className="text-gold font-medium">{destination.name}</span>
          </li>
        </ol>
      </nav>

      {/* Hero Header */}
      <header className="bg-gradient-to-b from-navy-light to-navy py-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-gold mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Înapoi la pagina principală
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-8 h-8 text-gold" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                  Vacanțe în {destination.name} 6
                </h1>
                <p className="text-white/70 mt-2 max-w-2xl">
                  Descoperă {destination.count} pachete turistice în {destination.name}. 
                  Circuite, sejururi și city break-uri la cele mai bune prețuri.
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap gap-4 md:gap-6">
              <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
                <Package className="w-5 h-5 text-gold" />
                <div>
                  <p className="text-2xl font-bold text-white">{destination.count}</p>
                  <p className="text-xs text-white/60">Pachete</p>
                </div>
              </div>
              
              {priceMin && priceMin < 999999 && (
                <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
                  <Euro className="w-5 h-5 text-gold" />
                  <div>
                    <p className="text-2xl font-bold text-white">de la {priceMin}€</p>
                    <p className="text-xs text-white/60">Preț minim</p>
                  </div>
                </div>
              )}
              
              {transportTypes.length > 0 && (
                <div className="flex items-center gap-2 bg-white/5 rounded-xl px-4 py-3">
                  <Plane className="w-5 h-5 text-gold" />
                  <div>
                    <p className="text-sm font-medium text-white capitalize">
                      {transportTypes.slice(0, 2).join(', ')}
                    </p>
                    <p className="text-xs text-white/60">Transport</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* SEO Content Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white/5 rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
            <Star className="w-5 h-5 text-gold" />
            De ce să alegi o vacanță în {destination.name}?
          </h2>
          <p className="text-white/70 leading-relaxed">
            {destination.name} este una dintre cele mai populare destinații turistice pentru românii care caută experiențe memorabile. 
            Cu {destination.count} oferte disponibile, găsești pachete pentru toate preferințele și bugetele.
            {priceMin && priceMin < 999999 ? ' Prețurile încep de la doar ' + priceMin + '€ per persoană.' : ''}
            {transportTypes.includes('avion') ? ' Se ofera transport cu avionul sau autocarul din București și alte orașe majore.' : ''}
          </p>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
          <Calendar className="w-6 h-6 text-gold" />
          {destination.count} Pachete Turistice în {destination.name}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {destination.packages?.map((pkg) => (
            <PackageCard key={pkg.id} package={pkg} useSeoUrl={true} />
          ))}
        </div>
        
        {destination.packages?.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/60">Nu există pachete disponibile momentan pentru această destinație.</p>
          </div>
        )}
      </section>

      {/* SEO Footer Content */}
      <section className="bg-navy-light border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="text-xl font-semibold text-white mb-4">
            Informații utile despre vacanțele în {destination.name}
          </h2>
          <div className="grid md:grid-cols-2 gap-6 text-white/70 text-sm">
            <div>
              <h3 className="text-white font-medium mb-2">Cum rezervi?</h3>
              <p>
                Selectează pachetul preferat, verifică detaliile și finalizează rezervarea online. 
              </p>
            </div>
            <div>
              <h3 className="text-white font-medium mb-2">Plecări garantate</h3>
              <p>
                Majoritatea pachetelor au plecări garantate din București. 
                Verifică detaliile fiecărui pachet pentru informații despre alte orașe de plecare.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
